import { countBy } from 'lodash'
import type { ResolvedPlaidItemSyncStatus } from './bankAccounts.helpers'
import type { FinancialAccount } from '@/reducers/finances/financialAccountsReducer'

export interface ConnectedInstitution {
  plaidItemId: number
  name: string
  /** Any account on the item — update mode resolves the link token from its Plaid item. */
  accountId: number
  hasSyncError: boolean
}

/**
 * One entry per connected Plaid item, for the menu's Existing Connections section.
 * Manual accounts are excluded — they have no Plaid item to update. A bank connected
 * twice appears twice: those are genuinely separate connections, disambiguated by the
 * mask of the account each entry resolves to, following `formatAccountName`. The mask is
 * appended only where a name would otherwise repeat, so the common case stays clean.
 *
 * Expects accounts already narrowed by `isVisibleAccount`, so removed connections
 * never reach the menu — offering one strands the member, since the update flow
 * needs a live access token the item no longer has.
 */
export const getConnectedInstitutions = (
  financialAccounts: FinancialAccount[],
  syncItems: ResolvedPlaidItemSyncStatus[]
): ConnectedInstitution[] => {
  const erroredPlaidItemIds = new Set(
    syncItems
      .filter((item) => item.resolvedErrorClassification)
      .map((item) => item.plaidItemId)
  )

  const byPlaidItemId = new Map<
    number,
    ConnectedInstitution & { mask: string | null }
  >()
  for (const account of financialAccounts) {
    const { plaidItemId, plaidInstitutionName } = account
    if (plaidItemId === null || byPlaidItemId.has(plaidItemId)) continue
    byPlaidItemId.set(plaidItemId, {
      plaidItemId,
      name: plaidInstitutionName ?? 'Bank account',
      accountId: account.id,
      hasSyncError: erroredPlaidItemIds.has(plaidItemId),
      mask: account.mask,
    })
  }

  const entries = [...byPlaidItemId.values()]
  const nameCounts = countBy(entries, 'name')

  const named = entries.map(({ mask, ...institution }) => {
    if (!mask || nameCounts[institution.name] === 1) return institution
    return { ...institution, name: `${institution.name} ${mask}` }
  })

  return named.sort((a, b) => a.name.localeCompare(b.name))
}
